// error class for fetch response that carries status, data, and the original response

/**
 * Specialized error for HTTP requests that preserves **status**, **headers**, and the **server body**.
 *
 * Use this to surface rich API failure details to your UI instead of losing them in a generic Error.
 *
 * Typical flow:
 * 1) Your fetch helper reads the response body (JSON or text).
 * 2) If `!response.ok`, it **throws** `new ResponseError(message, context)`.
 * 3) The UI `catch` block can inspect `e.status`, `e.data`, `e.text`, etc., to decide what to show.
 *
 * @example
 * // fetch helper
 * const res = await fetch(url, init);
 * const raw = await res.text();                 // read once
 * let data = null; try { data = raw && JSON.parse(raw); } catch { /* not JSON * }
 * if (!res.ok) {
 *   throw new ResponseError(
 *     data?.message || res.statusText || "Request failed",
 *     {
 *       status: res.status,
 *       statusText: res.statusText,
 *       url: res.url,
 *       headers: Object.fromEntries(res.headers.entries()),
 *       data,            // parsed JSON if any
 *       text: raw,       // raw string body (fallback if not JSON)
 *     }
 *   );
 * }
 * return { status: res.status, data, headers: Object.fromEntries(res.headers.entries()) };
 *
 * @example
 * // UI usage (e.g., Login page)
 * try {
 *   const { status, data } = await login({ email, password });
 *   setSuccess(data?.message || "Login successful");
 * } catch (e) {
 *   if (e instanceof ResponseError) {
 *     // Rich info available:
 *     // e.status (e.g., 409), e.statusText, e.data (parsed JSON), e.text (raw), e.headers, e.url
 *     if (e.status === 409 && e.data?.meta?.verified === false) {
 *       setError(e.data.message);                // server message
 *       setShowResendVerification(true);
 *     } else {
 *       setError(e.data?.message || `Login failed (${e.status})`);
 *     }
 *   } else {
 *     // Network/unknown
 *     setError(e?.message || "Unexpected error");
 *   }
 * }
 */

/**
 * @class ResponseError
 * @extends Error
 *
 * @param {string} message
 *   Human-friendly error message. Prefer the server’s `data.message` if available;
 *   otherwise use `response.statusText` or a sensible fallback like "Request failed".
 *
 * @param {Object} [options]
 * @param {number} [options.status=0]
 *   HTTP status code (e.g., 400, 401, 409, 422, 500). `0` typically indicates a network error.
 *
 * @param {string} [options.statusText=""]
 *   HTTP status text (e.g., "Bad Request", "Unauthorized", "Conflict").
 *
 * @param {string} [options.url=""]
 *   The request URL that produced this error. Useful for logging and debugging.
 *
 * @param {Object.<string,string>} [options.headers={}]
 *   Plain object of response headers. Often includes `content-type`, `retry-after`, etc.
 *
 * @param {*} [options.data=null]
 *   Parsed response body if it was JSON; otherwise `null`. Commonly shaped as:
 *   `{ success: boolean, message?: string, meta?: object, errors?: object }`
 *
 * @param {string} [options.text=""]
 *   Raw response body string (useful when parsing fails or server returns non-JSON).
 *
 * @param {unknown} [options.cause]
 *   Original error that triggered this one (e.g., a network exception). Placed on `error.cause`
 *   so you can preserve the stack chain in modern runtimes.
 *
 * @property {number} status          HTTP status code
 * @property {string} statusText      HTTP status text
 * @property {string} url             Request URL
 * @property {Object.<string,string>} headers Response headers
 * @property {*} data                 Parsed JSON body (or null)
 * @property {string} text            Raw body string (or "")
 * @property {unknown} [cause]        Original underlying error, if any
 *
 * @remarks
 * - Always read the response body **once** in your fetch helper; pass both the parsed JSON (`data`)
 *   and the raw string (`text`) here. This avoids body-stream reuse errors.
 * - Prefer throwing `ResponseError` only for **non-2xx** statuses. Let success return a structured
 *   object (e.g., `{ ok: true, status, data }`) so your UI path stays simple.
 */

export default class ResponseError extends Error {
  // constructor(message, { response = null, data = null, cause = null } = {}) {
  constructor(
    message,
    {
      response = null,
      status,
      statusText,
      url,
      headers,
      data,
      text,
      cause = null,
    } = {}
  ) {
    super(message);
    this.name = "ResponseError";
    this.response = response;
    this.status = response?.status ?? 0; // 0 for network/timeout
    this.statusText = response?.statusText ?? ""; // human-readable message associated with the status code
    this.statusMessage = response?.statusMessage ?? ""; // human-readable message associated with the status code
    this.url = response?.url; // final url after any redirects
    this.headers = headers ?? {};
    this.data = data; // parsed JSON if any
    this.text = text ?? ""; // raw text if not JSON
    if (cause) this.cause = cause; // keep original error
  }
}
